
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    storeSize: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, storeSize: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Demopyyntö vastaanotettu!",
        description: "Tiimimme ottaa sinuun yhteyttä pian demonvn ajankohdasta.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        storeSize: "",
        message: ""
      });
    }, 1500);
  };

  return (
    <section id="contact" className="section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Valmis muuttamaan <span className="gradient-text">Verkkokauppasi</span>?
            </h2>
            <p className="text-lg text-appella-darkGray mb-8">
              Varaa personoitu demo ja katso, miten OrderSales.AI:n tekoälyratkaisut voivat auttaa sinua 
              kasvattamaan myyntiä, parantamaan asiakastyytyväisyyttä ja optimoimaan toimintaasi.
            </p>
            
            <div className="space-y-6 mt-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                  <span className="text-blue-500 font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Pyydä demo</h3>
                  <p className="text-appella-darkGray">
                    Täytä lomake varataksesi verkkokauppaasi räätälöidyn demon.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                  <span className="text-blue-500 font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Räätälöity käyttöönotto</h3>
                  <p className="text-appella-darkGray">
                    Asiantuntijamme työskentelevät kanssasi räätälöidäkseen tekoälyratkaisun verkkokauppasi tarpeisiin.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                  <span className="text-blue-500 font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Muuta liiketoimintaasi</h3>
                  <p className="text-appella-darkGray">
                    Näe verkkokauppasi muuttuvan tehokkaammaksi, kannattavammaksi ja asiakaslähtöisemmäksi.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold mb-6">Pyydä demo</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Koko nimi</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Nimesi"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Sähköposti</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="sinä@esimerkki.fi"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Puhelinnumero</Label>
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+358 40 123 4567"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Yrityksen nimi</Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder="Verkkokauppasi"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="storeSize">Verkkokaupan koko</Label>
                  <Select 
                    value={formData.storeSize} 
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Valitse koko" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Pieni (alle 500 000€/vuosi)</SelectItem>
                      <SelectItem value="medium">Keskikokoinen (500 000€ - 2M€/vuosi)</SelectItem>
                      <SelectItem value="large">Suuri (yli 2M€/vuosi)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Miten voimme auttaa?</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Kerro tarpeistasi..."
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Lähetetään..." : "Pyydä demo"}
                </Button>
                
                <p className="text-xs text-center text-muted-foreground mt-4">
                  Lähettämällä lomakkeen hyväksyt{" "}
                  <a href="#" className="underline">tietosuojakäytännömme</a> ja{" "}
                  <a href="#" className="underline">käyttöehdot</a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
